import type { NextRequest } from 'next/server';
import { NextResponse } from 'next/server';

import { routing } from '@/i18n/routing';

export const LOCALE_COOKIE_KEY = 'NEXT_LOCALE';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365; // 1 year in seconds

const supportedLocales = routing.locales;
function isLocale(segment: (typeof supportedLocales)[number]): boolean {
    return supportedLocales.includes(segment);
}

function detectLocaleInPath(segments: (typeof supportedLocales)[number][]): {
    locale: (typeof supportedLocales)[number];
    pathnameWithoutLocale: string;
} | null {
    if (segments.length === 0) return null;

    // Prefer first segment (standard /en/... pattern)
    if (isLocale(segments[0])) {
        return {
            locale: segments[0],
            pathnameWithoutLocale: '/' + segments.slice(1).join('/')
        };
    }

    // Fallback: last segment as locale (e.g. /dashboard/en)
    if (segments.length > 1 && isLocale(segments[segments.length - 1])) {
        return {
            locale: segments[segments.length - 1],
            pathnameWithoutLocale: '/' + segments.slice(0, -1).join('/')
        };
    }

    return null;
}

export function middleware(request: NextRequest) {
    const { pathname, search } = request.nextUrl;
    const segments = pathname.split('/').filter((segment) => segment.length > 0);

    const detected = detectLocaleInPath(segments as (typeof supportedLocales)[number][]);

    if (detected) {
        const redirectUrl = new URL(detected.pathnameWithoutLocale + search, request.url);
        const response = NextResponse.redirect(redirectUrl, 302);

        response.cookies.set(LOCALE_COOKIE_KEY, detected.locale, {
            path: '/',
            sameSite: 'lax',
            maxAge: COOKIE_MAX_AGE_SECONDS
        });

        return response;
    }

    return NextResponse.next();
}

export const config = {
    matcher: ['/((?!api|_next|_vercel|.*\\..*).*)']
};
